import React, { useCallback, useEffect, useState } from 'react';
import { Plus, LogOut, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import AdminLoginForm from './admin/AdminLoginForm';
import AdminBlogEditor from './admin/AdminBlogEditor';
import AdminPostList from './admin/AdminPostList';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY_FORM = {
  id: '', title: '', excerpt: '', content: '',
  category: 'DDV', image: '/images/exterior.webp', published: true
};

const getErrorMessage = async (response, fallback) => {
  try {
    const data = await response.json();
    return typeof data.detail === 'string' ? data.detail : fallback;
  } catch {
    return fallback;
  }
};

const AdminBlogPanel = () => {
  const [authState, setAuthState] = useState('checking');
  const [csrfToken, setCsrfToken] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const endSession = useCallback(() => {
    setAuthState('anonymous');
    setCsrfToken('');
    setPosts([]);
    setEditingPost(null);
    setIsCreating(false);
  }, []);

  const adminFetch = useCallback(async (path, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (options.method && options.method !== 'GET' && csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers,
      credentials: 'include'
    });
    if (response.status === 401) endSession();
    return response;
  }, [csrfToken, endSession]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminFetch('/api/admin/blog');
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Napaka pri nalaganju člankov'));
      setPosts(await response.json());
    } catch (error) {
      if (authState === 'authenticated') toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [adminFetch, authState]);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/admin/session`, { credentials: 'include' })
      .then(async (response) => {
        if (!active) return;
        if (!response.ok) return endSession();
        const data = await response.json();
        setCsrfToken(data.csrf_token);
        setAuthState('authenticated');
      })
      .catch(() => active && endSession());
    return () => { active = false; };
  }, [endSession]);

  useEffect(() => {
    if (authState === 'authenticated') fetchPosts();
  }, [authState, fetchPosts]);

  const handleLogin = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Prijava ni uspela'));
      const data = await response.json();
      setCsrfToken(data.csrf_token);
      setAuthState('authenticated');
      toast.success('Prijava uspešna');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminFetch('/api/admin/logout', { method: 'POST' });
    } finally {
      endSession();
      toast.success('Odjava uspešna');
    }
  };

  const resetEditor = () => {
    setEditingPost(null);
    setIsCreating(false);
    setFormData(EMPTY_FORM);
  };

  const startCreate = () => {
    setEditingPost(null);
    setIsCreating(true);
    setFormData(EMPTY_FORM);
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setIsCreating(false);
    setFormData({ ...post, content: post.content || '' });
  };

  const savePost = async () => {
    if (!formData.id || !formData.title || !formData.excerpt) {
      toast.error('Izpolnite vsa obvezna polja');
      return;
    }
    setLoading(true);
    const creating = isCreating;
    const path = creating ? '/api/admin/blog' : `/api/admin/blog/${editingPost.id}`;
    try {
      const response = await adminFetch(path, {
        method: creating ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, 'Shranjevanje ni uspelo'));
      toast.success(creating ? 'Članek uspešno ustvarjen' : 'Članek uspešno posodobljen');
      resetEditor();
      await fetchPosts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati ta članek?')) return;
    const response = await adminFetch(`/api/admin/blog/${postId}`, { method: 'DELETE' });
    if (response.ok) {
      toast.success('Članek uspešno izbrisan');
      await fetchPosts();
    } else toast.error(await getErrorMessage(response, 'Napaka pri brisanju'));
  };

  const togglePublish = async (postId) => {
    const response = await adminFetch(`/api/admin/blog/${postId}/publish`, { method: 'PATCH' });
    if (response.ok) {
      const data = await response.json();
      toast.success(data.published ? 'Članek objavljen' : 'Članek skrit');
      await fetchPosts();
    } else toast.error(await getErrorMessage(response, 'Napaka pri spremembi statusa'));
  };

  if (authState === 'checking') {
    return <div className="min-h-screen bg-[#F6F2EA] grid place-items-center" data-testid="admin-session-loading">Preverjam prijavo…</div>;
  }
  if (authState !== 'authenticated') return <AdminLoginForm onLogin={handleLogin} loading={loading} />;

  return (
    <div className="min-h-screen bg-[#F6F2EA]" data-testid="admin-panel">
      <header className="bg-[#0E1114] text-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-[#C9A462] hover:text-white" data-testid="admin-home-link" aria-label="Nazaj na domačo stran"><ArrowLeft className="w-5 h-5" /></a>
            <h1 className="font-serif text-xl">Blog Admin Panel</h1>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-[#B9B3A7] hover:text-white" data-testid="admin-logout-button"><LogOut className="w-4 h-4" />Odjava</button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-[#15181C]" data-testid="admin-post-count">Blog članki ({posts.length})</h2>
          <button onClick={startCreate} className="flex items-center gap-2 bg-[#C9A462] text-[#0E1114] font-semibold px-5 py-2.5 hover:bg-[#B8944F]" data-testid="admin-create-post-button"><Plus className="w-4 h-4" />Nov članek</button>
        </div>
        {(isCreating || editingPost) && <AdminBlogEditor formData={formData} setFormData={setFormData} isCreating={isCreating} loading={loading} onCancel={resetEditor} onSave={savePost} />}
        <AdminPostList posts={posts} loading={loading} onEdit={startEdit} onDelete={deletePost} onTogglePublish={togglePublish} />
      </main>
    </div>
  );
};

export default AdminBlogPanel;